import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { BackgroundCheckApiResponse } from './vendors.service/vendors-service.interface';

@Injectable({
  providedIn: 'root',
})
export class PatronService {
  private apiUrl = environment.apiUrl;
  constructor(private http: HttpClient) {}

  loadCandidates() {
    return this.http.get<any>(`${this.apiUrl}v1/patrons`).pipe(
      catchError((err: HttpErrorResponse) => {
        console.error('Failed to load candidates', err);
        return of([]);
      }),
    );
  }

  createPatron(payload: CreatePatronRequest) {
    return this.http.post<CreatePatronResponse>(`${this.apiUrl}v1/patrons`, payload).pipe(
      tap({
        error: (err: HttpErrorResponse) => {
          console.error('Failed to create patron', err);
        },
      }),
    );
  }

  getPatron(candidateId: string): Observable<GetPatronResponse> {
    return this.http.get<GetPatronResponse>(`${this.apiUrl}v1/patrons/${candidateId}`).pipe(
      tap({
        error: (err: HttpErrorResponse) => {
          console.error('Failed to load patron', err);
        },
      }),
    );
  }

  getPatronGraph(candidateId: string): Observable<GetPatronGraphResponse> {
    return this.http.get<GetPatronGraphResponse>(`${this.apiUrl}v1/patron/${candidateId}/graph-data`).pipe(
      tap({
        error: (err: HttpErrorResponse) => {
          console.error('Failed to load patron graph', err);
        },
      }),
    );
  }

  searchPatrons(searchParam: string): Observable<SearchPatronResponse> {
    return this.http.get<SearchPatronResponse>(`${this.apiUrl}v1/patrons/search?q=${searchParam}`).pipe(
      tap({
        error: (err: HttpErrorResponse) => {
          console.error('Failed to search patrons', err);
        },
      }),
    );
  }

  addToSearchQueue(candidateId: string) {
    return this.http.post(`${this.apiUrl}v1/patrons/search-queue`, { candidateId }).pipe(
      tap({
        error: (err: HttpErrorResponse) => {
          console.error('Failed to add to search queue', err);
        },
      }),
    );
  }

  getSearchQueue(page = 1, limit = 10, q: string | null = null) {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('isArchive', 'false');
    if (q) {
      params = params.set('q', q);
    }

    return this.http.get<SearchQueueResponse>(`${this.apiUrl}v1/patrons/search-queue`, { params }).pipe(
      tap({
        error: (err: HttpErrorResponse) => {
          console.error('Failed to get search queue', err);
        },
      }),
    );
  }

  archiveSearchQueue(id: number) {
    return this.http.patch(`${this.apiUrl}v1/patrons/search-queue/${id}/archive`, {}).pipe(
      tap({
        error: (err: HttpErrorResponse) => {
          console.error('Failed to archive search queue item', err);
        },
      }),
    );
  }

  unarchiveSearchQueue(id: number) {
    return this.http.patch(`${this.apiUrl}v1/patrons/search-queue/${id}/unarchive`, {}).pipe(
      tap({
        error: (err: HttpErrorResponse) => {
          console.error('Failed to unarchive search queue item', err);
        },
      }),
    );
  }

  getSearchQueueById(id: string) {
    return this.http.get<GetSearchQueueByIdResponse>(`${this.apiUrl}v1/patrons/search-queue/${id}`).pipe(
      tap({
        error: (err: HttpErrorResponse) => {
          console.error('Failed to get search queue by id', err);
        },
      }),
    );
  }

  getBackgroundChecksForSearchQueue(id: string) {
    return this.http
      .get<BackgroundChecksForSearchQueueResponse>(`${this.apiUrl}v1/patrons/search-queue/${id}/background-checks`)
      .pipe(
        tap({
          error: (err: HttpErrorResponse) => {
            console.error('Failed to get background checks for search queue', err);
          },
        }),
      );
  }

  runBackgroundCheckForPatron(searchQueueId: string, apiNames: string[]) {
    return this.http
      .post<void>(`${this.apiUrl}v1/patrons/search-queue/${searchQueueId}/run-background-checks`, {
        apiNames,
      })
      .pipe(
        tap({
          error: (err: HttpErrorResponse) => {
            console.error('Failed to run background checks for patron', err);
          },
        }),
      );
  }
  runPlusRulesForSearchQueue(searchQueueId: string, plusRules: string[]) {
    return this.http.post(`${this.apiUrl}v1/patrons/search-queue/${searchQueueId}/run-plus-rules`, { plusRules }).pipe(
      tap({
        error: (err: HttpErrorResponse) => {
          console.error('Failed to run plus rules for search queue', err);
        },
      }),
    );
  }

  // investigate(candidate_id: any, verification: any) {
  //   return this.http.get<any[]>(
  //     `${this.apiUrl}candidate/screening/assessment_requests/${candidate_id}?assessment_type=${verification}`,
  //   );
  // }
  // Method for hard delete
  // hardDeleteCandidate(candidateId: string) {
  //   return this.http.delete(`${this.apiUrl}candidate/screening/details/hard/${candidateId}`).pipe(
  //     catchError((err: HttpErrorResponse) => {
  //       console.error('Failed to hard delete candidate', err);
  //       return of(null);
  //     }),
  //   );
  // }

  // // Method for soft delete
  // softDeleteCandidate(candidateId: string) {
  //   return this.http.delete(`${this.apiUrl}candidate/screening/delete/soft/${candidateId}`).pipe(
  //     catchError((err: HttpErrorResponse) => {
  //       console.error('Failed to soft delete candidate', err);
  //       return of(null);
  //     }),
  //   );
  // }

  // manualScreening(updateData: any) {
  //   return this.http.post(`${this.apiUrl}ats/manual_screening`, updateData).pipe(
  //     catchError((err: HttpErrorResponse) => {
  //       console.error('Failed to perform manual screening:', err);
  //       return throwError(() => err);
  //     })
  //   );
  // }
}

export interface GetPatronGraphDataItem {
  api_name: string;
  assessment_decision: string;
  response: string;
}

export interface GetPatronGraphResponse {
  status: number;
  message: string;
  data: GetPatronGraphDataItem[];
}

export interface GetPatronData {
  id: string;
  candidateId: string;
  name: string;
  dateOfBirth: string;
  email: string;
  jobName: string;
  ssn: string;
  phoneNumber: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  department: string;
  refId: string;
  documentType: string;
  driverLicenseNumber: string;
  dlState: string;
  bgIndexCode: string;
  assessmentSource: string;
}

export interface GetPatronResponse {
  status: number;
  message: string;
  data: GetPatronData;
}

export interface SearchPatronResponse {
  status: number;
  message: string;
  data: GetPatronData[];
}

export interface SearchQueueItem {
  id: number;
  requestId: number;
  candidateId: string;
  candidateName: string;
  createdAt: string;
  isArchive: boolean;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  totalCount: number;
}

export interface SearchQueueResponse {
  status: number;
  message: string;
  data: SearchQueueItem[];
  pagination: PaginationInfo;
}

export interface GetSearchQueueByIdResponse {
  status: number;
  message: string;
  searchQueue: SearchQueueItem;
  candidate: {
    name: string;
    id: number;
  };
  uniqueBackgroundChecks: string[];
}

export interface BackgroundChecksForSearchQueueResponse {
  status: number;
  message: string;
  data: BackgroundCheckApiResponse[];
}

export type PatronBackgroundCheckType =
  | 'Gaming Exclusion List'
  | 'Sanctions'
  | 'ITRAK Exclusion List'
  | 'US Criminal'
  | 'Federal Criminal'
  | 'Federal Civil'
  | 'Sex Offender Registry'
  | 'News'
  | 'SAR Narrative Generator';

export const allPatronBackgroundCheckTypes: PatronBackgroundCheckType[] = [
  'Gaming Exclusion List',
  'Sanctions',
  'ITRAK Exclusion List',
  'US Criminal',
  'Federal Criminal',
  'Federal Civil',
  'Sex Offender Registry',
  'News',
  'SAR Narrative Generator',
];

export interface CreatePatronRequest {
  name: string;
  date_of_birth: string; // YYYY-MM-DD
  ssn?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  ref_id?: string;
}

export interface CreatePatronResponse {
  status: number;
  message: string;
}
